import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError, type Install } from '../api';
import { Alert, Button, Card, PageHeader, Spinner } from '../components/ui';

export default function Devices() {
  const [installs, setInstalls] = useState<Install[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const justLinked = params.get('linked') === '1';

  async function load() {
    try {
      setInstalls(await api.installs());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load devices');
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function rename(hash: string, current: string | null) {
    const label = prompt('Device label', current ?? '');
    if (label === null) return;
    await api.setDeviceLabel(hash, label.trim() || null).catch(() => {});
    await load();
  }

  async function unlink(hash: string) {
    if (!confirm('Unlink this device from your account?')) return;
    await api.unlinkDevice(hash).catch(() => {});
    await load();
  }

  return (
    <div>
      <PageHeader title="Linked Devices" subtitle="SwarmAI installs connected to your account." />
      {justLinked && (
        <div className="mb-4">
          <Alert kind="success">Device linked successfully.</Alert>
        </div>
      )}
      {error && <Alert kind="error">{error}</Alert>}
      {!installs ? (
        <Spinner />
      ) : installs.length === 0 ? (
        <Card>
          <p className="text-sm text-slate-500">
            No devices linked. Run <code className="text-slate-700">swarmai hub link</code> in your SwarmAI install to
            connect this account.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {installs.map((d) => (
            <Card key={d.installIdHash} className="flex items-center justify-between py-4">
              <div>
                <div className="font-semibold text-slate-900">
                  {d.deviceLabel ?? `${d.osHint ?? 'device'} · ${d.installIdHash.slice(0, 8)}…`}
                </div>
                <p className="mt-0.5 text-xs text-slate-400">
                  Linked {new Date(d.linkedAt).toLocaleDateString()}
                  {d.lastSeenAt && ` · last seen ${new Date(d.lastSeenAt).toLocaleDateString()}`}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => rename(d.installIdHash, d.deviceLabel)}>
                  Rename
                </Button>
                <Button variant="danger" onClick={() => unlink(d.installIdHash)}>
                  Unlink
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
